import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { useAppSelector } from '../../../../redux/ReduxHooks';
import PortSlotIcon from '../../../Icons/PortSlotIcon';
import '../../../Drawers/Controller/Controller.css';

interface DriveUnitPortProps {
  id: string;
  portNumber: number;
  selected: number;
  onClick: () => void;
  /**
   * `dialog` (default): same as hardware cards — confirm before taking a port used elsewhere.
   * `inline`: select the port and let the parent show `portSelector.conflict.message` when it is in use elsewhere.
   */
  conflictPresentation?: 'dialog' | 'inline';
}

const DriveUnitPort = ({ id, portNumber, selected, onClick, conflictPresentation = 'dialog' }: DriveUnitPortProps) => {
  const { t } = useTranslation();
  const [conflictOpen, setConflictOpen] = useState(false);
  const driveUnitPorts = useAppSelector((state) => state.application.driveUnitPorts);
  const claimedByThisCard =
    driveUnitPorts.some((row: { driveUnitPort: number; electronic: string }) => row.driveUnitPort === portNumber && row.electronic === id) ||
    selected === portNumber;
  const claimedByAnotherCard = driveUnitPorts.some(
    (row: { driveUnitPort: number; electronic: string }) => row.driveUnitPort === portNumber && row.electronic !== id
  );
  /** Grey + conflict dialog only when another device uses the port and this card is not on that port. */
  const usedByOtherControl = claimedByAnotherCard && !claimedByThisCard;
  const showConflictDialog = conflictPresentation === 'dialog' && usedByOtherControl;

  const handleActivate = () => {
    if (usedByOtherControl && conflictPresentation === 'dialog') {
      setConflictOpen(true);
      return;
    }
    onClick();
  };

  const handleContinue = () => {
    setConflictOpen(false);
    onClick();
  };

  const handleCancel = () => {
    setConflictOpen(false);
  };

  return (
    <Fragment>
      <PortSlotIcon
        portNumber={portNumber}
        selected={selected === portNumber}
        portUsedByOtherDevice={usedByOtherControl}
        onClick={handleActivate}
        ariaHasPopup={showConflictDialog ? 'dialog' : undefined}
        ariaLabel={t('portSelector.port_aria', { port: portNumber, defaultValue: 'Port {{port}}' })}
      />
      <Dialog
        open={conflictPresentation === 'dialog' && conflictOpen}
        onClose={handleCancel}
        aria-labelledby="drive-unit-port-conflict-title"
        aria-describedby="drive-unit-port-conflict-desc"
      >
        <DialogTitle id="drive-unit-port-conflict-title">{t('portSelector.conflict.title', { defaultValue: 'Port in use' })}</DialogTitle>
        <DialogContent>
          <DialogContentText id="drive-unit-port-conflict-desc" color="text.warning">
            {t('portSelector.conflict.message', {
              defaultValue: 'This port is already used by another mechanism or electronic in your code.',
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>{t('portSelector.conflict.cancel', { defaultValue: 'Cancel' })}</Button>
          <Button onClick={handleContinue} color="warning" variant="contained" autoFocus>
            {t('portSelector.conflict.continue', { defaultValue: 'Continue' })}
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

export default DriveUnitPort;
